import React from "react";
import { Page, Text, View, Document, StyleSheet, Image } from "@react-pdf/renderer";
import paymentMethods from "utils/static/paymentMethods";
import { hasElements } from "utils/arrays";
import { dateToString, isSameDate } from "utils/dateUtils";
import { Tenant } from "utils/tenants";
import { formatCurrency } from "utils/currency";

const getPaymentMethod = (value) => paymentMethods.find((x) => x.value === value);
const Money = ({ amount, currency }) => formatCurrency(amount, currency.locale, currency.isoCode);

// Create styles
const styles = StyleSheet.create({
    page: {
        paddingTop: 65,
        paddingBottom: 65,
        paddingHorizontal: 60,
    },
    header: {
        fontSize: 10,
        flexDirection: "row",
        marginBottom: 10,
        justifyContent: "flex-start",
    },
    image: {
        height: 64,
    },
    companyInfo: {
        marginLeft: 10,
    },
    companyName: {
        fontSize: 14,
        fontWeight: "bold",
    },
    title: {
        fontSize: 20,
    },
    divider: {
        width: "100%",
        height: 1,
        backgroundColor: "black",
        marginVertical: 10,
    },

    pageNumber: {
        position: "absolute",
        fontSize: 10,
        bottom: 30,
        left: 0,
        right: 0,
        textAlign: "center",
        color: "grey",
    },

    detail: {
        borderWidth: 1,
        borderColor: "gray",
        width: "100%",
    },

    tableHead: {
        flexDirection: "row",
        alignContent: "flex-start",
        borderBottomWidth: 1,
        borderBottomColor: "gray",
    },

    columnHeader: {
        fontSize: 10,
        fontWeight: "bold",
        color: "blue",
        fontStyle: "italic",
        padding: 2,
        flex: 1,
        textAlign: "left",
        marginLeft: 5,
    },

    row: {
        flexDirection: "row",
        alignContent: "flex-start",
    },

    cell: {
        fontSize: 9,
        padding: 2,
        flex: 1,
        textAlign: "left",
        marginLeft: 5,
    },
    paymentMethods: {
        flexDirection: "column",
        justifyContent: "flex-start",
    },
    paymentMethod: {
        fontSize: 12,
        fontWeight: "bold",
        marginTop: 10,
    },
});

// Create Document Component
const MyDocument = ({ salesData, initialDateInMillis, finalDateInMillis, withUsers = false, documentIssuer, currency }) => {
    const initialDate = new Date(initialDateInMillis);
    const finalDate = new Date(finalDateInMillis);
    const isSameDay = isSameDate(initialDate, finalDate, "day");

    const getDifferentPaymentMethods = () => {
        if (hasElements(salesData)) {
            return salesData.reduce((arr, item) => {
                return arr.includes(item.paymentMethod) ? arr : [...arr, item.paymentMethod];
            }, []);
        }
        return [];
    };

    const getTotalAmountByPaymentMethod = (paymentMethod) => {
        if (hasElements(salesData)) {
            const filteredRows = salesData.filter((x) => x.paymentMethod === paymentMethod);
            return filteredRows.reduce((acc, item) => acc + item.amount, 0);
        }
        return 0;
    };

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.header} fixed>
                    {<Image style={styles.image} src={Tenant.getLogo()} />}
                    <View style={styles.companyInfo}>
                        <Text fixed style={styles.companyName}>
                            {documentIssuer?.name}
                        </Text>

                        <Text fixed>{documentIssuer?.address}</Text>
                        <Text fixed>Phone: {documentIssuer?.phoneNumber}</Text>
                        <Text fixed>Fax: {documentIssuer?.fax}</Text>
                        <Text fixed>Email: {documentIssuer?.email}</Text>
                    </View>
                    <View style={{ flex: 1 }} />
                    <View>
                        <Text fixed style={styles.title}>
                            Sales {isSameDay && "today"}
                        </Text>
                        {!isSameDay && (
                            <>
                                <Text fixed style={{ fontSize: 10, alignSelf: "flex-end" }}>
                                    From: {dateToString(initialDate, false)}
                                </Text>
                                <Text fixed style={{ fontSize: 10, alignSelf: "flex-end" }}>
                                    To: {dateToString(finalDate, false)}
                                </Text>
                            </>
                        )}
                        <Text fixed style={{ fontSize: 10, alignSelf: "flex-end" }}>
                            Report date: {dateToString(new Date(), false)}
                        </Text>
                    </View>
                </View>

                <View style={styles.divider} fixed />

                <View style={styles.detail}>
                    <View style={styles.tableHead}>
                        <View style={styles.columnHeader}>
                            <Text>Category</Text>
                        </View>
                        <View style={{ ...styles.columnHeader }}>
                            <Text>Payment Method</Text>
                        </View>
                        {withUsers && (
                            <>
                                <View style={{ ...styles.columnHeader }}>
                                    <Text>Responsible</Text>
                                </View>
                                <View style={{ ...styles.columnHeader }}>
                                    <Text>Date</Text>
                                </View>
                            </>
                        )}
                        <View style={{ ...styles.columnHeader, textAlign: "right" }}>
                            <Text>Amount</Text>
                        </View>
                    </View>
                    {salesData &&
                        salesData.map((item, index, arr) => (
                            <View key={item.category + item.paymentMethod} style={styles.row}>
                                <View style={styles.cell}>
                                    <Text>
                                        {index > 0 ? (arr[index - 1].category === item.category ? "" : item.category) : item.category}
                                    </Text>
                                </View>
                                <View style={styles.cell}>
                                    <Text>{getPaymentMethod(item.paymentMethod)?.label}</Text>
                                </View>
                                {withUsers && (
                                    <>
                                        <View style={styles.cell}>
                                            <Text>{item.responsible}</Text>
                                        </View>
                                        <View style={styles.cell}>
                                            <Text>{item.dateString}</Text>
                                        </View>
                                    </>
                                )}
                                <View style={{ ...styles.cell, textAlign: "right", fontWeight: "bold" }}>
                                    <Text>
                                        {item.amount && Number(item.amount) > 0 ? <Money amount={item.amount} currency={currency} /> : ""}
                                    </Text>
                                </View>
                            </View>
                        ))}
                </View>

                <View style={styles.divider} />

                <View style={styles.paymentMethods}>
                    {getDifferentPaymentMethods().map((item) => {
                        return (
                            <View key={item}>
                                <Text style={styles.paymentMethod}>
                                    {getPaymentMethod(item)?.label}:{" "}
                                    <Money amount={getTotalAmountByPaymentMethod(item)} currency={currency} />
                                </Text>
                            </View>
                        );
                    })}
                </View>

                <View style={styles.divider} />

                <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
            </Page>
        </Document>
    );
};

export default MyDocument;
